"use client";

import { Lock, LoaderCircle, Search } from "lucide-react";
import { useEffect, useId, useState } from "react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { AppLocale } from "@/features/resume/content/dashboard-copy";
import { searchNocTitles } from "@/features/resume/services/resume-service";
import type { NocSelection, NocSuggestion } from "@/types/resume";

const copy = {
  en: {
    label: "Job Title You Are Looking For · National Occupational Classification (NOC)",
    placeholder: "Start typing: Web developer, Teacher, 21234",
    placeholderLocked: "Job title hidden",
    subtitle: "Start typing to search, then select an official NOC title from the list.",
    subtitleRequired: "This field is required.",
    subtitleLocked: "The official code and title are shown with full access.",
    searching: "Searching official NOC titles…",
    empty: "No matching official title found.",
    selectionRequired: "Select an official NOC title from the list.",
  },
  fr: {
    label: "Titre de l’emploi recherché · Classification nationale des professions (CNP)",
    placeholder: "Commencer à taper : Développeur Web, Infirmière, 21234",
    placeholderLocked: "Titre de profession masqué",
    subtitle: "Saisissez votre métier, puis sélectionnez un titre CNP officiel dans la liste.",
    subtitleRequired: "Ce champ est obligatoire.",
    subtitleLocked: "Le code et le titre officiels s’affichent avec l’accès complet.",
    searching: "Recherche dans les titres officiels de la CNP…",
    empty: "Aucun titre officiel correspondant.",
    selectionRequired: "Veuillez sélectionner un titre officiel dans la liste.",
  },
} as const;

export function NocTypeahead({ tenant, locale, value, onChange, error, maskCode = false, disabled = false }: { tenant: string; locale: AppLocale; value: NocSelection | null; onChange(value: NocSelection | null): void; error?: string; maskCode?: boolean; disabled?: boolean }) {
  const listId = useId();
  const text = copy[locale];
  const [inputValue, setInputValue] = useState("");
  const [results, setResults] = useState<NocSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const query = value ? (locale === "fr" ? value.title_fr : value.title_en) : inputValue;

  useEffect(() => {
    if (query.trim().length < 3 || value || maskCode) return;
    const timeout = window.setTimeout(() => {
      setLoading(true);
      void searchNocTitles(tenant, query.trim(), locale)
        .then((response) => {
          setResults(response.data);
          setOpen(true);
        })
        .finally(() => setLoading(false));
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [locale, maskCode, query, tenant, value]);

  function select(result: NocSuggestion) {
    onChange(result);
    setInputValue("");
    setOpen(false);
  }

  return <div className="relative sm:col-span-2">
    <Label htmlFor="noc-title" className={maskCode ? "text-amber-700" : undefined}>{text.label}<span className="text-destructive">*</span></Label>
    <p className={`mt-1 text-xs ${maskCode ? "text-amber-700" : "text-muted-foreground"}`}>{text.subtitle}{!maskCode ? ` ${text.subtitleRequired}` : ""}</p>
    <div className="relative mt-1">
      {maskCode ? <Lock className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" /> : <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />}
      <Input id="noc-title" role="combobox" aria-expanded={open && !maskCode} aria-controls={listId} aria-autocomplete="list" aria-invalid={Boolean(error)} value={query} placeholder={maskCode ? text.placeholderLocked : text.placeholder} className="h-10 pl-9 pr-32" readOnly={disabled} onChange={(event) => { setInputValue(event.target.value); onChange(null); setOpen(true); }} />
      {loading && <LoaderCircle className="absolute right-3 top-1/2 size-4 -translate-y-1/2 animate-spin" />}
      {value && !maskCode && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-primary">{locale === "fr" ? "CNP" : "NOC"} {value.code}</span>}
      {maskCode && <span className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1 whitespace-nowrap rounded-full border border-amber-300 bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">🔒 {locale === "fr" ? "CNP" : "NOC"} 00000</span>}
    </div>
    {maskCode && <p className="mt-1 text-xs text-muted-foreground">{text.subtitleLocked}</p>}
    {open && query.trim().length >= 3 && !value && !maskCode && <ul id={listId} role="listbox" className="absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded-xl border bg-card p-1 shadow-lg">{results.map((result) => <li key={result.code} role="option" aria-selected="false"><button type="button" className="flex w-full items-start justify-between gap-3 rounded-lg px-3 py-2 text-left hover:bg-muted focus:bg-muted focus:outline-none" onClick={() => select(result)}><span>{result.title}</span><span className="shrink-0 font-semibold text-primary">{locale === "fr" ? "CNP" : "NOC"} {result.code}</span></button></li>)}{!loading && results.length === 0 && <li className="px-3 py-2 text-sm text-muted-foreground">{text.empty}</li>}</ul>}
    {loading && <p className="mt-1 text-xs text-muted-foreground" role="status">{text.searching}</p>}
    {error && <p className="mt-1 text-xs text-destructive" role="alert">{text.selectionRequired}</p>}
  </div>;
}
